import { useEffect, useMemo, useRef, useState } from "react";
import qrcode from "qrcode-generator";
import { onPhoneRemote, phoneRemote, phoneRemoteAvailable, type PhoneRemoteStatus } from "../remote/phoneRemote.js";
import { requireFeature } from "../library/proFeatures.js";

/**
 * Settings → Phone remote (roadmap Section 25): pair a phone, tablet or laptop
 * on this Wi-Fi to control the TV, and remove the ones you no longer want.
 * Only inside the Android app; renders nothing on the website.
 *
 * Off until the first pairing. Pairing shows a QR code (phones scan it) and the
 * address and six digits (a laptop types them); the code works for two minutes.
 */

/** The QR code as one SVG path: no canvas, scales sharply on any TV. */
function QrCode({ text }: { text: string }) {
  const { size, path } = useMemo(() => {
    const qr = qrcode(0, "M");
    qr.addData(text);
    qr.make();
    const count = qr.getModuleCount();
    let d = "";
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) d += `M${col + 4} ${row + 4}h1v1h-1z`;
      }
    }
    return { size: count + 8, path: d };
  }, [text]);
  return (
    <svg className="phone-remote__qr" viewBox={`0 0 ${size} ${size}`} role="img" aria-label="QR code to pair a phone" shapeRendering="crispEdges">
      <rect width={size} height={size} fill="#fff" />
      <path d={path} fill="#000" />
    </svg>
  );
}

function ago(ms: number): string {
  if (!ms) return "never";
  const minutes = Math.round((Date.now() - ms) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return new Date(ms).toLocaleDateString();
}

export function PhoneRemoteCard() {
  const [status, setStatusRaw] = useState<PhoneRemoteStatus | null>(null);
  // When the code on screen stops working, as a clock time.
  const [deadline, setDeadline] = useState(0);
  const setStatus = (next: PhoneRemoteStatus | null) => {
    setStatusRaw(next);
    setDeadline(next?.pairingCode ? Date.now() + next.pairingLeftMs : 0);
  };
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [, tick] = useState(0);
  const pairingRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const pairRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!phoneRemoteAvailable()) return;
    void phoneRemote.status().then(setStatus).catch(() => setStatus(null));
    return onPhoneRemote("phones", (next) => setStatus(next));
  }, []);

  // The pairing countdown, and closing the code when it runs out.
  useEffect(() => {
    if (!deadline) return;
    const timer = window.setInterval(() => {
      tick((n) => n + 1);
      if (Date.now() >= deadline) void phoneRemote.status().then(setStatus).catch(() => undefined);
    }, 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  /*
   * Opening pairing swaps the focused "Pair a phone" for the code, so nothing
   * was focused and nothing scrolled: the QR code sat wherever it landed, often
   * cut off (owner, 2026-10-10). It is centred on screen with Cancel focused, and
   * closing it puts focus back on "Pair a phone".
   */
  const pairing = Boolean(status?.url && status?.pairingCode);
  const wasPairing = useRef(false);
  useEffect(() => {
    // Waits for the TV's answer: a button is disabled (unfocusable) while busy.
    if (busy || pairing === wasPairing.current) return;
    wasPairing.current = pairing;
    if (pairing) {
      pairingRef.current?.scrollIntoView({ block: "center" });
      cancelRef.current?.focus({ preventScroll: true });
    } else if (document.activeElement === document.body || !document.activeElement) {
      pairRef.current?.focus({ preventScroll: true });
      pairRef.current?.scrollIntoView({ block: "nearest" });
    }
  }, [pairing, busy]);

  if (!status) return null;

  const run = async (work: () => Promise<PhoneRemoteStatus | null>, startsIt = false) => {
    // The phone remote is Pro (ADR 2026-10-10). Turning it off, cancelling and removing a phone
    // are always allowed; starting pairing or turning it on asks.
    if (startsIt && !requireFeature("phoneRemote")) return;
    setBusy(true);
    try {
      const next = await work();
      if (next) setStatus(next);
    } finally {
      setBusy(false);
    }
  };

  const pairUrl = status.url && status.pairingCode ? `${status.url}#pair=${status.pairingCode}` : null;

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">Phone remote</h2>
      <p className="settings-card-desc">
        Use a phone, tablet or laptop on this Wi-Fi as a remote and second screen: buttons, typing, the channel list,
        search and favorites. It works on your home network only, and only for devices you pair here.
      </p>

      {pairUrl ? (
        <div className="phone-remote__pairing" ref={pairingRef}>
          <QrCode text={pairUrl} />
          <div className="phone-remote__steps">
            <p><b>On a phone:</b> scan this code with the camera.</p>
            <p>
              <b>On a laptop or tablet:</b> open <span className="phone-remote__address">{status.url}</span> and type
            </p>
            {status.localUrl && (
              <p className="settings-row-hint">
                or <span className="phone-remote__address">{status.localUrl}</span>, which stays the same when this TV&apos;s address changes
              </p>
            )}
            <p className="phone-remote__code" aria-label={`Code ${status.pairingCode!.split("").join(" ")}`}>
              {status.pairingCode}
            </p>
            <p className="settings-row-hint">
              The code works for {Math.max(0, Math.ceil((deadline - Date.now()) / 1000))} more seconds. The device must be on the same Wi-Fi as this TV.
            </p>
            <button ref={cancelRef} type="button" className="ui-btn" onClick={() => void run(phoneRemote.cancelPairing)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="settings-row">
          <span className="settings-row-label">{status.phones.length ? "Add another" : "Get started"}</span>
          <button ref={pairRef} type="button" className="ui-btn ui-btn--primary" onClick={() => void run(phoneRemote.startPairing, true)} disabled={busy || !status.address}>
            Pair a phone
          </button>
        </div>
      )}
      {!status.address && <p className="settings-row-hint">This TV has no Wi-Fi or network address right now.</p>}
      {status.enabled && !status.running && (
        <p className="settings-row-hint">The remote is not listening{status.error ? `: ${status.error}` : ""}. Turn it off and on again.</p>
      )}

      {status.phones.length > 0 && (
        <ul className="phone-remote__list">
          {status.phones.map((phone) => (
            <li key={phone.id} className="settings-row">
              <span className="settings-row-label">
                {phone.name}
                <span className="settings-row-hint">{phone.connected ? " · connected now" : ` · last used ${ago(phone.lastSeen)}`}</span>
              </span>
              <button
                type="button"
                className="ui-btn ui-btn--ghost"
                onClick={() => {
                  if (confirmRemove !== phone.id) {
                    setConfirmRemove(phone.id);
                    return;
                  }
                  setConfirmRemove(null);
                  void run(() => phoneRemote.revoke(phone.id));
                }}
                disabled={busy}
                aria-label={confirmRemove === phone.id ? `Press again to remove ${phone.name}` : `Remove ${phone.name}`}
              >
                {confirmRemove === phone.id ? "Press again to remove" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {status.enabled && (
        <div className="settings-row">
          <span className="settings-row-label">Phone remote</span>
          <button type="button" className="ui-btn" onClick={() => void run(() => phoneRemote.setEnabled(false))} disabled={busy}>
            Turn off
          </button>
        </div>
      )}
    </div>
  );
}
